import styled from '@emotion/styled';
import {
  Checkbox,
  FormControlLabel as _FormControlLabel,
  List,
  ListItem as _ListItem,
  ListItemSecondaryAction as _ListItemSecondaryAction,
  ListItemText as _ListItemText,
  Paper as _Paper,
  Typography,
} from '@material-ui/core';
import Help from '@material-ui/icons/Help';
import { createElement, FC, Fragment } from 'react';
import { FormattedMessage } from 'react-intl';

import ProductPricing from '../../../../entity/ProductPricing';
import { Option } from '../../../../generated/graphql';
import theme from '../../../../theme';
import { calculateSelectedOptionTotalPoint } from '../../../../utility/calculatePoint';
import { useGetIsPC } from '../../../../utilityHooks/useGetIsPC';
import { SPACING_REGULAR, SPACING_UNIT } from '../../../constant/spacing';
import { flexibleLayoutContainer } from '../../../styleUtility/flexibleLayout';
import { InputtedCreationSet } from '../../../types/InputtedCreationSet';
import Spinner from '../Spinner';
import OptionPriceDetails from './OptionPriceDetails';

type Props = {
  isPointRequired: boolean;
  productPricing?: ProductPricing;
  creationSet?: InputtedCreationSet;
  pricingMerchandiseOptions: ReadonlyArray<Option>;
  selectedPricingMerchandiseOptionIds: Array<string>;
  onChangeSwitch: (id: string, checked: boolean) => void;
};

const OptionMerchandiseList: FC<Props> = ({
  isPointRequired,
  productPricing,
  creationSet,
  pricingMerchandiseOptions,
  selectedPricingMerchandiseOptionIds,
  onChangeSwitch,
}) => {
  const isPC = useGetIsPC();
  return (
    <>
      {pricingMerchandiseOptions.length > 0 ? (
        <Paper>
          {isPC ? (
            <List disablePadding>
              {pricingMerchandiseOptions.map((pricingMerchandiseOption: Option) => {
                const optionPoint = creationSet
                  ? calculateSelectedOptionTotalPoint(creationSet, pricingMerchandiseOptions, [
                      pricingMerchandiseOption.id,
                    ])
                  : 0;

                return (
                  <ListItem button divider key={`option-${pricingMerchandiseOption.id}`}>
                    {/* オプション名表示 */}
                    <ListItemOptionName
                      primary={
                        <>
                          <OptionName variant="subtitle1">
                            {pricingMerchandiseOption.name}
                            {pricingMerchandiseOption.helpUrlForDirector ? (
                              <HelpAnchor
                                href={pricingMerchandiseOption.helpUrlForDirector}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                <HelpIcon />
                              </HelpAnchor>
                            ) : null}
                          </OptionName>
                        </>
                      }
                    />
                    {/* 価格明細表示 */}
                    <ListItemFormat
                      primary={
                        <OptionPriceDetails
                          isPointRequired={isPointRequired}
                          productPricing={productPricing}
                          creationSet={creationSet}
                          pricingMerchandiseOption={pricingMerchandiseOption}
                        />
                      }
                    />
                    {/* 合計価格 & オプション選択 */}
                    <ListItemPriceAndCheck>
                      <SwitchWithPoint>
                        <AlignRight>
                          {isPointRequired ? (
                            productPricing ? (
                              <RequiredPoint>
                                {productPricing.currency}
                                {((optionPoint || 0) * productPricing.flatRate).toLocaleString()}
                              </RequiredPoint>
                            ) : (
                              <Spinner />
                            )
                          ) : null}
                        </AlignRight>

                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedPricingMerchandiseOptionIds.includes(pricingMerchandiseOption.id)}
                              onChange={(e) => onChangeSwitch(pricingMerchandiseOption.id, e.target.checked)}
                            />
                          }
                          label={<FormattedMessage id="project_editing_page.option_setting.checkbox_label" />}
                          labelPlacement="bottom"
                        />
                      </SwitchWithPoint>
                    </ListItemPriceAndCheck>
                  </ListItem>
                );
              })}
            </List>
          ) : (
            <List disablePadding>
              {pricingMerchandiseOptions.map((pricingMerchandiseOption: Option) => {
                const optionPoint = creationSet
                  ? calculateSelectedOptionTotalPoint(creationSet, pricingMerchandiseOptions, [
                      pricingMerchandiseOption.id,
                    ])
                  : 0;

                return (
                  <ListItem button divider key={`option-${pricingMerchandiseOption.id}`}>
                    <FlexTop>
                      <FlexLeft>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={selectedPricingMerchandiseOptionIds.includes(pricingMerchandiseOption.id)}
                              onChange={(e) => onChangeSwitch(pricingMerchandiseOption.id, e.target.checked)}
                            />
                          }
                          label={''}
                          labelPlacement="bottom"
                        />
                        {/* オプション名表示 */}
                        <ListItemOptionName
                          primary={
                            <>
                              <OptionName variant="subtitle1">
                                {pricingMerchandiseOption.name}
                                {pricingMerchandiseOption.helpUrlForDirector ? (
                                  <HelpAnchor
                                    href={pricingMerchandiseOption.helpUrlForDirector}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  />
                                ) : null}
                              </OptionName>
                            </>
                          }
                        />
                      </FlexLeft>
                      <div>
                        {/* 合計価格 & オプション選択 */}
                        <ListItemPriceAndCheck>
                          <SwitchWithPoint>
                            <AlignRight>
                              {isPointRequired ? (
                                productPricing ? (
                                  <RequiredPoint>
                                    {productPricing.currency}
                                    {((optionPoint || 0) * productPricing.flatRate).toLocaleString()}
                                  </RequiredPoint>
                                ) : (
                                  <Spinner />
                                )
                              ) : null}
                            </AlignRight>
                          </SwitchWithPoint>
                        </ListItemPriceAndCheck>
                      </div>
                    </FlexTop>
                    {/* 価格明細表示 */}
                    <ListItemFormat
                      primary={
                        <OptionPriceDetails
                          isPointRequired={isPointRequired}
                          productPricing={productPricing}
                          creationSet={creationSet}
                          pricingMerchandiseOption={pricingMerchandiseOption}
                        />
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </Paper>
      ) : (
        <Container>
          <Typography variant="body2">
            <FormattedMessage id="project_editing_page.option_setting.no_item" />
          </Typography>
        </Container>
      )}
    </>
  );
};

const RequiredPoint = styled(Typography)`
  ${flexibleLayoutContainer({
    alignItems: 'center',
  })}
  font-weight: 700
`;

RequiredPoint.defaultProps = { variant: 'body1', color: 'primary' };

const SwitchWithPoint = styled('div')`
  ${flexibleLayoutContainer({
    alignItems: 'center',
  })}
`;

const Paper = styled(_Paper)`
  ${theme.breakpoints.down('md')} {
    margin-bottom: 20px;
  }
`;

const ListItem = styled(_ListItem)`
  ${theme.breakpoints.down('md')} {
    flex-direction: column;
    padding-left: 10px;
    padding-right: 10px;
  }
`;

const ListItemOptionName = styled(_ListItemText)`
  text-align: center;
  word-wrap: break-word;
  width: 0%;
  ${theme.breakpoints.down('md')} {
    max-width: 77%;
    text-align: left;
    word-break: break-word;
  }
`;

const ListItemFormat = styled(_ListItemText)`
  padding-right: 200px;
  width: 50%;
  ${theme.breakpoints.down('md')} {
    padding: 8px;
    width: 100%;
    box-shadow: 0 0 4px 0 #00000029;
  }
`;

const ListItemPriceAndCheck = styled(_ListItemSecondaryAction)`
  padding-right: ${SPACING_REGULAR}px;
  ${theme.breakpoints.down('md')} {
    position: relative;
    padding: 0;
    transform: none;
  }
`;

const OptionName = styled(Typography)`
  font-weight: bold;
`;

const HelpAnchor = styled('a')`
  display: inline-flex;
  align-items: center;
  vertical-align: -0.3rem;
  margin-left: ${SPACING_UNIT}px;
  color: ${theme.palette.secondary.main};
  text-decoration: none;
`;

const HelpIcon = styled(Help)`
  margin-right: 2px;
  font-size: 20px;
`;

const AlignRight = styled('div')`
  text-align: right;
`;

const Container = styled(Paper)`
  padding: ${SPACING_REGULAR}px;
`;

const FlexTop = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const FlexLeft = styled('div')`
  display: flex;
  align-items: center;
  margin-right: 10px;
`;

const FormControlLabel = styled(_FormControlLabel)`
  ${theme.breakpoints.down('md')} {
    margin: 0;
  }
`;

export default OptionMerchandiseList;
